import React, { useMemo } from 'react';
import { useAppDispatch } from 'appRoot/Store';
import ModalContainer from 'design-v2/common/ModalContainer';
import { Box, IconButton, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { IBooking } from 'shared-types/SharedTypes';
import { floorplanBookingsActions } from 'floorplan-v2/app/reducers/bookings/slice';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';
import color from 'design-v2/globals/colors/colors.module.css';
import ActionButton from 'design-v2/common/ActionButton';
import styles from './style.module.scss';
import ConflictItem from './ConflictItem';
import BookingService from 'app/services/booking/booking.service';
import DateUtilsService from 'external-services/date-utils-service';

const BookingConflictsModal = () => {
  const dispatch = useAppDispatch();
  const conflictBookings  = useSelector(
    (state: DiaryRootState) => state.bookingReducer.bookingConflicts?.overlapConflicts);
  const showBookingConflictsModal = useSelector((state: DiaryRootState) => state.rootDisplayReducer.showBookingConflictsModal);
  const conflictTableId = useSelector((state: DiaryRootState) => state.rootDisplayReducer.conflictTableId);

  const handleClose = () => {
    // close the modal
    dispatch(rootDisplayActions.setShowBookingConflictsModal(false));
    // reset the conflictTableId
    dispatch(rootDisplayActions.setConflictTableId(null));
  };

  const _handleBookingClicked = (activeBooking: IBooking) => {
    handleClose();
    BookingService.handleModalBookingClicked(activeBooking, dispatch);
  }

  const handleStatusChange = (bookingId: string, statusId: string) => {
    // Handle status change
    dispatch(
      floorplanBookingsActions.setSelectedBookingForStatusModal({
        statusId,
        bookingId,
      })
    );
  };
  const conflictsByDate = useMemo(() => {
    const groups: Record<string, any[]> = {};
    conflictBookings?.forEach(conflict => {
      const dateKey = DateUtilsService.dateToTimeStr(conflict.firstBooking.time, 'ddd, D MMMM');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(conflict);
    });
    return groups;
  }, [conflictBookings]);

  /**
   * Filter the conflict bookings to get the name of the specific table,
   * eg: when click on a table with conflict icon in the floorplan, it will show the conflicts for that table only.
  **/
  const filteredConflictBookingName = useMemo(() => {
    return conflictBookings[0]?.table?.name || '';
  }, [conflictBookings]);

  if (!showBookingConflictsModal) {
    return null;
  }

  return (
    <ModalContainer
      closeTimeoutMS={0}
      isOpen={showBookingConflictsModal}
      onRequestClose={handleClose}
      >
      <Box className={styles.modalContent}>
        {/* Header */}
        <Box className={styles.header}>
          <Box className={styles.headerContent}>
            <WarningIcon className={styles.warningIcon} />
            {!!conflictTableId || conflictBookings.length < 2 ? (
              <Typography variant="h6" className={styles.title}>
                Booking Conflict - {filteredConflictBookingName}
              </Typography>) : (
              <Typography variant="h6" className={styles.title}>
                Booking conflicts - All
              </Typography>
            )}
          </Box>
          <IconButton size="medium" aria-label="Close booking conflicts" onClick={handleClose}>
            <CloseIcon className={color.clrGreyMedium} />
          </IconButton>
        </Box>
        {/* Conflict Bookings List */}
        <Box className={styles.conflictsList}>
          {Object.entries(conflictsByDate).map(([dateHeader, conflicts]) => (
            <Box key={dateHeader}>
              <Typography className={styles.conflictDateHeader}>{dateHeader}</Typography>
              {conflicts.map(conflict => (
                <ConflictItem
                  key={`${conflict.firstBooking._id}-${conflict.secondBooking._id}`}
                  conflict={conflict}
                  conflictTableId={conflictTableId}
                  handleStatusChange={handleStatusChange}
                  handleBookingClicked={_handleBookingClicked}
                />
              ))}
            </Box>
          ))}
        </Box>

        {/* Done Button */}
        <Box className={styles.doneButtonContainer}>
          <ActionButton variant={'primary'} size={'regular'} text={'Done'} handleClick={handleClose} />
        </Box>
      </Box>

    </ModalContainer>
  );
};

export default BookingConflictsModal;
